import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", postal_code: "", city: "",
    password: "",
    accept_cgu: false, accept_privacy: false, marketing_consent: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_cgu) return toast.error("Vous devez accepter les CGU");
    if (!form.accept_privacy) return toast.error("Vous devez accepter la politique de confidentialité");
    if (form.password.length < 8) return toast.error("Mot de passe minimum 8 caractères");
    if (!form.email.trim() && !form.phone.trim()) {
      return toast.error("Renseignez un e-mail OU un numéro de téléphone");
    }
    if (!form.address.trim() || !form.city.trim() || !form.postal_code.trim()) {
      return toast.error("Adresse, code postal et ville sont requis");
    }
    setBusy(true);
    const payload = {
      first_name: form.first_name, last_name: form.last_name,
      password: form.password, address: form.address, postal_code: form.postal_code,
      city: form.city, accept_cgu: form.accept_cgu, accept_privacy: form.accept_privacy,
      marketing_consent: form.marketing_consent,
    };
    if (form.email.trim()) payload.email = form.email.trim();
    if (form.phone.trim()) payload.phone = form.phone.trim();
    const r = await register(payload);
    setBusy(false);
    if (r.ok) {
      toast.success("Compte créé — votre fiche de consentement a été générée.");
      nav("/espace-client");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] hero-bg py-14 px-6 grid place-items-center">
      <div className="w-full max-w-3xl bg-white p-8 md:p-10 rounded-2xl border border-slate-100 card-lux">
        <div className="text-gold uppercase tracking-widest text-xs font-semibold">Inscription</div>
        <h1 className="font-heading text-3xl text-primary-blue mt-2">Créez votre compte client</h1>
        <p className="text-slate-600 mt-2 text-sm">
          Renseignez au moins un e-mail <em>ou</em> un numéro de téléphone. Une fiche de consentement PDF privée sera générée automatiquement.
        </p>

        <form onSubmit={submit} className="mt-6 grid md:grid-cols-2 gap-5">
          <div>
            <Label>Prénom <span className="text-red-600">*</span></Label>
            <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required className="mt-1 h-11" data-testid="reg-first" />
          </div>
          <div>
            <Label>Nom <span className="text-red-600">*</span></Label>
            <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required className="mt-1 h-11" data-testid="reg-last" />
          </div>
          <div>
            <Label>E-mail <span className="text-slate-500 text-xs">(e-mail ou téléphone requis)</span></Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 h-11" data-testid="reg-email" />
          </div>
          <div>
            <Label>Téléphone <span className="text-slate-500 text-xs">(e-mail ou téléphone requis)</span></Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="06 12 34 56 78 ou +33 6 12…" className="mt-1 h-11" data-testid="reg-phone" />
          </div>
          <div className="md:col-span-2">
            <Label>Mot de passe (min. 8 caractères) <span className="text-red-600">*</span></Label>
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required className="mt-1 h-11" data-testid="reg-password" />
          </div>
          <div className="md:col-span-2">
            <Label>Adresse <span className="text-red-600">*</span></Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} required className="mt-1 h-11" data-testid="reg-address" />
          </div>
          <div>
            <Label>Code postal <span className="text-red-600">*</span></Label>
            <Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} required className="mt-1 h-11" data-testid="reg-postal" />
          </div>
          <div>
            <Label>Ville <span className="text-red-600">*</span></Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} required className="mt-1 h-11" data-testid="reg-city" />
          </div>

          <div className="md:col-span-2 space-y-3 bg-sky-blue/40 p-5 rounded-lg border border-primary-blue/10">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.accept_cgu} onCheckedChange={(v) => set("accept_cgu", !!v)} className="mt-1" data-testid="reg-accept-cgu" />
              <span className="text-sm text-slate-700">
                J'ai lu et j'accepte les <Link to="/cgu" target="_blank" className="text-primary-blue underline">Conditions Générales d'Utilisation</Link>. <span className="text-red-600">*</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.accept_privacy} onCheckedChange={(v) => set("accept_privacy", !!v)} className="mt-1" data-testid="reg-accept-privacy" />
              <span className="text-sm text-slate-700">
                J'ai lu et j'accepte la <Link to="/confidentialite" target="_blank" className="text-primary-blue underline">Politique de confidentialité</Link>. <span className="text-red-600">*</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => set("marketing_consent", !!v)} className="mt-1" data-testid="reg-consent-marketing" />
              <span className="text-sm text-slate-700">J'accepte de recevoir des informations et conseils (facultatif).</span>
            </label>
          </div>

          <div className="md:col-span-2">
            <Button type="submit" disabled={busy} className="w-full bg-primary-blue text-white h-12" data-testid="reg-submit">
              <UserPlus size={18} className="mr-2" /> {busy ? "Création…" : "Créer mon compte"}
            </Button>
          </div>
        </form>
        <div className="mt-6 text-sm text-slate-600">
          Déjà un compte ? <Link to="/connexion" className="text-primary-blue underline" data-testid="reg-to-login">Se connecter</Link>
        </div>
      </div>
    </div>
  );
}
